import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';
import DarkModeWrapper from '../../../components/DarkModeWrapper';

const SkillsPreview = () => {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const { isDark } = useTheme();

  const coreSkills = [
    {
      id: 1,
      name: 'React',
      icon: 'Code',
      proficiency: 95,
      color: 'from-blue-500 to-cyan-500',
      description: 'Advanced React development with hooks, context, and performance optimization'
    },
    {
      id: 2,
      name: 'Node.js',
      icon: 'Server',
      proficiency: 90,
      color: 'from-green-500 to-emerald-500',
      description: 'Backend development with Express, APIs, and microservices architecture'
    },
    {
      id: 3,
      name: 'Python',
      icon: 'Code2',
      proficiency: 88,
      color: 'from-yellow-500 to-orange-500',
      description: 'Data analysis, automation, and backend development with Django/Flask'
    },
    {
      id: 4,
      name: 'AWS',
      icon: 'Cloud',
      proficiency: 85,
      color: 'from-orange-500 to-red-500',
      description: 'Cloud infrastructure, serverless functions, and deployment automation'
    },
    {
      id: 5,
      name: 'TypeScript',
      icon: 'FileCode',
      proficiency: 92,
      color: 'from-blue-600 to-indigo-600',
      description: 'Type-safe development with advanced TypeScript patterns'
    },
    {
      id: 6,
      name: 'MongoDB',
      icon: 'Database',
      proficiency: 87,
      color: 'from-green-600 to-teal-600',
      description: 'NoSQL database design, aggregation pipelines, and optimization'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <DarkModeWrapper sectionName="core-technologies">
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              Core Technologies
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Expertise across the full technology stack with proven proficiency in modern frameworks and tools
            </p>
          </motion.div>

          {/* Skills Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
          >
            {coreSkills.map((skill) => (
              <motion.div
                key={skill.id}
                variants={itemVariants}
                onMouseEnter={() => setHoveredSkill(skill.id)}
                onMouseLeave={() => setHoveredSkill(null)}
                className="relative group"
              >
                <div className={`rounded-xl p-6 border skill-card ${isDark ? 'border-gray-700' : 'border-border hover:border-primary/30'} transition-premium shadow-sm hover:shadow-premium`}>
                  {/* Skill Header */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${skill.color} flex items-center justify-center`}>
                      <Icon name={skill.icon} size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary">{skill.name}</h3>
                      <p className="text-sm text-text-secondary">{skill.proficiency}% Proficiency</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className={`w-full ${isDark ? 'bg-gray-700' : 'bg-muted'} rounded-full h-2 skill-progress-bg`}>
                      <motion.div
                        className={`h-2 rounded-full bg-gradient-to-r ${skill.color}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${skill.proficiency}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.2 }}
                      />
                    </div>
                  </div>

                  {/* Description - Shows on Hover */}
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{
                      opacity: hoveredSkill === skill.id ? 1 : 0,
                      height: hoveredSkill === skill.id ? 'auto' : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="text-sm text-text-secondary leading-relaxed">
                      {skill.description}
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center"
          >
            <div className={`bg-gradient-to-r ${isDark ? 'from-primary/20 to-brand-purple/20 border-primary/40' : 'from-primary/10 to-brand-purple/10 border-primary/20'} rounded-2xl p-8 border`}>
              <h3 className="text-2xl font-bold text-text-primary mb-4">
                Want to see the complete skill set?
              </h3>
              <p className="text-text-secondary mb-6 max-w-xl mx-auto">
                Explore my comprehensive technical expertise, certifications, and hands-on experience across various technologies and frameworks.
              </p>
              <Link to="/skills-technical-mastery-showcase">
                <Button
                  variant="default"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="hover-lift"
                >
                  View All Skills
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </DarkModeWrapper>
  );
};

export default SkillsPreview;